import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { FormData } from '../Config/types'
import { defaultInputValues } from '../Config/defaults'
import { getNewFormData, getUpdatedURL, isMobileDevice } from '../utils'
import ErrorDisplay from './ErrorDisplay'
import { SliderWithInput, OptionalNumberField } from './FormFields'
import {
  CASH_FLOW_DISCOUNT_MIN,
  CASH_FLOW_DISCOUNT_MAX,
  CASH_FLOW_DISCOUNT_STEP,
  MARGIN_OF_SAFETY_MIN,
  MARGIN_OF_SAFETY_MAX,
  MARGIN_OF_SAFETY_STEP,
  NUMBER_OF_YEARS_MIN,
  NUMBER_OF_YEARS_MAX,
  GROWTH_RATE_MIN,
  GROWTH_RATE_STEP,
  MARKET_CAP_FCF_RATIO_MIN,
  MARKET_CAP_FCF_RATIO_STEP,
} from '../Config/validationRules'
import { TextField, Box, Stack, Paper, Button } from '@mui/material'
import { Backspace } from '@mui/icons-material'

export interface ValuationInputFormError {
  id: string
  message: string
  type: 'error' | 'warning' | 'info'
}

interface ValuationInputFormProps {
  onSubmit: (formData: FormData) => void
  onReset: () => void
  isLoading: boolean
  errors: ValuationInputFormError[]
  onDismissError: (errorId: string) => void
  onDrawerClose?: () => void
}

export const ValuationInputForm: React.FC<ValuationInputFormProps> = ({
  onSubmit,
  onReset,
  isLoading,
  errors,
  onDismissError,
  onDrawerClose,
}) => {
  const [formData, setFormData] = useState<FormData>(defaultInputValues)
  const didEffectRun = useRef(false)
  const [symbolsInput, setSymbolsInput] = useState<string>(defaultInputValues.symbols.join(", "))
  const formRef = useRef<HTMLFormElement>(null)

  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    if (didEffectRun.current) return
    didEffectRun.current = true

    const params = new URLSearchParams(location.search)
    const newFormData = getNewFormData(params)

    setFormData(newFormData)
    setSymbolsInput(newFormData.symbols.join(", "))

    return () => {
      didEffectRun.current = false
    }
  }, [location.search])

  // Sync symbolsInput with formData.symbols to prevent clearing on view switches
  // Only update if the symbols field is not focused (user not actively typing)
  useEffect(() => {
    const currentSymbolsString = formData.symbols.join(", ")
    const isSymbolsFieldFocused = document.activeElement?.id === 'symbols'
    
    if (!isSymbolsFieldFocused) {
      setSymbolsInput(currentSymbolsString)
    }
  }, [formData.symbols])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    
    // List of optional fields that can be undefined
    const optionalFields = [
      'maximal_average_growth_rate',
      'maximal_market_cap_to_free_cash_flow_ratio',
      'average_growth_rate_annual',
      'average_market_cap_to_free_cash_flow_ratio'
    ]
    
    let updatedFormData: FormData
    
    if (optionalFields.includes(name) && value === '') {
      // For optional fields with empty value, set to undefined
      updatedFormData = {
        ...formData,
        [name]: undefined,
      }
    } else {
      // For other fields, parse the value
      updatedFormData = {
        ...formData,
        [name]: value ? parseFloat(value) : 0,
      }
    }
    
    setFormData(updatedFormData)
    const newParams = getUpdatedURL(updatedFormData)
    navigate({ search: newParams.toString() })
  }

  const handleChangeSlider = (e: Event, value: number, activeThumb: number) => {
    const target = e.target as HTMLInputElement
    const { name } = target
    if (!name) return

    const updatedFormData = {
      ...formData,
      [name]: value,
    }
    setFormData(updatedFormData)
    const newParams = getUpdatedURL(updatedFormData)
    navigate({ search: newParams.toString() })
  }

  const clearOptionalField = (fieldName: keyof FormData) => {
    const updatedFormData = {
      ...formData,
      [fieldName]: undefined,
    }
    setFormData(updatedFormData)
    const newParams = getUpdatedURL(updatedFormData)
    navigate({ search: newParams.toString() })
  }

  const handleReset = () => {
    setFormData(defaultInputValues)
    setSymbolsInput(defaultInputValues.symbols.join(", "))
    navigate({ search: '' })
    onReset()
  }

  const handleSymbolsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSymbolsInput(e.target.value)
  }

  const submitForm = (dataToSubmit: FormData) => {
    if (dataToSubmit.symbols.length === 0) {
      return
    }

    // Close drawer on mobile if callback provided
    if (onDrawerClose) {
      onDrawerClose()
    }

    // Blur all input fields to hide the keyboard on mobile devices
    if (isMobileDevice()) {
      const inputs = document.querySelectorAll('input')
      inputs.forEach((input) => input.blur())
    }

    onSubmit(dataToSubmit)
  }

  const handleKeyDownEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      
      // Parse symbols synchronously before submission
      const symbolsArray = symbolsInput.split(',').map(symbol => symbol.trim()).filter(symbol => symbol)
      const updatedFormData = {
        ...formData,
        symbols: symbolsArray,
      }
      
      // Update state and URL
      setFormData(updatedFormData)
      const newParams = getUpdatedURL(updatedFormData)
      navigate({ search: newParams.toString() })
      
      submitForm(updatedFormData)
    }
  }

  const handleSymbolsBlur = () => {
    const symbolsArray = symbolsInput.split(',').map(symbol => symbol.trim()).filter(symbol => symbol)
    const updatedFormData = {
      ...formData,
      symbols: symbolsArray,
    }
    setFormData(updatedFormData)
    const newParams = getUpdatedURL(updatedFormData)
    navigate({ search: newParams.toString() })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    submitForm(formData)
  }

  // Map errors to the format expected by ErrorDisplay
  const displayErrors = errors.map(error => ({
    ...error,
    timestamp: Date.now(),
  }))

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2.5,
        borderRadius: 1,
        bgcolor: 'grey.50',
      }}
    >
      <Box
        component="form"
        ref={formRef}
        onSubmit={handleSubmit}
        onKeyDown={(e) => {
          // Skip symbols field as it has its own Enter handler
          if (e.key === 'Enter' && e.target instanceof HTMLInputElement && (e.target as HTMLInputElement).id !== 'symbols') {
            e.preventDefault()
            handleSubmit(e as any)
          }
        }}
      >
        <Box sx={{ mb: 2 }}>
          <TextField
            id="symbols"
            name="symbols"
            label="Symbols (comma separated)"
            value={symbolsInput}
            onChange={handleSymbolsChange}
            onBlur={() => handleSymbolsBlur()}
            onKeyDown={handleKeyDownEnter}
            fullWidth
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <SliderWithInput
            label="Cash Flow Discount (annual)"
            name="annual_cash_flow_discount"
            value={formData.annual_cash_flow_discount}
            min={CASH_FLOW_DISCOUNT_MIN}
            max={CASH_FLOW_DISCOUNT_MAX}
            step={CASH_FLOW_DISCOUNT_STEP}
            id="annualCashFlowDiscount"
            onSliderChange={handleChangeSlider}
            onInputChange={handleChange}
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <SliderWithInput
            label="Margin of Safety"
            name="margin_of_safety"
            value={formData.margin_of_safety}
            min={MARGIN_OF_SAFETY_MIN}
            max={MARGIN_OF_SAFETY_MAX}
            step={MARGIN_OF_SAFETY_STEP}
            id="marginOfSafety"
            onSliderChange={handleChangeSlider}
            onInputChange={handleChange}
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <TextField
            type="number"
            slotProps={{ htmlInput: { 
              min: NUMBER_OF_YEARS_MIN, 
              max: NUMBER_OF_YEARS_MAX 
            } }}
            id="numberOfYears"
            name="number_of_years"
            label="Number of Years"
            value={formData.number_of_years}
            onChange={handleChange}
            fullWidth
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <OptionalNumberField
            name="maximal_average_growth_rate"
            label="Maximal Average Growth Rate (optional)"
            value={formData.maximal_average_growth_rate}
            id="maximalAverageGrowthRate"
            onChange={handleChange}
            onClear={() => clearOptionalField('maximal_average_growth_rate')}
            min={GROWTH_RATE_MIN}
            step={GROWTH_RATE_STEP}
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <OptionalNumberField
            name="maximal_market_cap_to_free_cash_flow_ratio"
            label="Maximal Market Cap To Free Cash Flow Ratio (optional)"
            value={formData.maximal_market_cap_to_free_cash_flow_ratio}
            id="maximalMarketCapToFreeCashFlowRatio"
            onChange={handleChange}
            onClear={() => clearOptionalField('maximal_market_cap_to_free_cash_flow_ratio')}
            min={MARKET_CAP_FCF_RATIO_MIN}
            step={MARKET_CAP_FCF_RATIO_STEP}
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <OptionalNumberField
            name="average_growth_rate_annual"
            label="Average Growth Rate Annual (optional)"
            value={formData.average_growth_rate_annual}
            id="averageGrowthRateAnnual"
            onChange={handleChange}
            onClear={() => clearOptionalField('average_growth_rate_annual')}
            min={GROWTH_RATE_MIN}
            step={GROWTH_RATE_STEP}
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <OptionalNumberField
            name="average_market_cap_to_free_cash_flow_ratio"
            label="Average Market Cap To Free Cash Flow Ratio (optional)"
            value={formData.average_market_cap_to_free_cash_flow_ratio}
            id="averageMarketCapToFreeCashFlowRatio"
            onChange={handleChange}
            onClear={() => clearOptionalField('average_market_cap_to_free_cash_flow_ratio')}
          />
        </Box>
        <Stack direction="row" spacing={1.25} sx={{ mt: 2.5, justifyContent: 'flex-end' }}>
          <Button
            type="button"
            onClick={handleReset}
            id="reset-button"
            variant="outlined"
            aria-label="Reset"
            sx={{ minWidth: 'auto', px: 1.5 }}
          >
            <Backspace />
          </Button>
          <Button
            type="submit"
            id="submit-button"
            variant="contained"
            color="primary"
            disabled={isLoading || !symbolsInput.trim()}
            sx={{ flexGrow: 1 }}
          >
            {isLoading ? "Submitting..." : "Submit"}
          </Button>
        </Stack>
      </Box>
      <ErrorDisplay 
        errors={displayErrors} 
        onDismiss={onDismissError} 
      />
    </Paper>
  )
}

export default ValuationInputForm
